<template>
  <BookBasicInfo :book="book" :breadcrumb="breadcrumb">
    <template #book-download>
      <OnboardingPopover
        v-if="ebookDownloadable"
        :is-visible="showReadBookOnboarding"
        position="topleft"
        :title="$label(`${onboardingLabel}.title`)"
        :text="$label(`${onboardingLabel}.content`)"
        :on-next="() => $router.push('/boeken')"
        :extra-items="[
          {
            text: $label(`${onboardingLabel}.infoText`)
          }
        ]"
      >
        <NuxtLink
          :to="`/lezen/${book.slug}`"
          data-testid="read-book-btn"
          class="reset-btn-styling read-book-btn d-flex align-items-center text-start"
          variant="link"
          @click.enter="readBookClicked"
        >
          <BookFlipPageIcon width="20px" height="20px" class="open-book-icon" />
          {{ $label('books.readEbook') }}
        </NuxtLink>
      </OnboardingPopover>

      <DownloadModal
        v-if="ebookDownloadable || audioBookDownloadable"
        :book="book"
        :audiobook-downloadable="audioBookDownloadable"
        :ebook-downloadable="ebookDownloadable"
      />
    </template>

    <template #book-actions>
      <AudioPlayerMiniSkeleton v-if="isLoadingDetails" />

      <template v-else>
        <div v-if="showBookPreviewSection" class="d-flex">
          <OnboardingPopover
            v-if="!audioBookDownloadable && audioBookPreview"
            :is-visible="showAudioPreviewOnboarding"
            position="topleft"
            :on-prev="() => $router.push('/boeken')"
            :title="$label(`${onboardingLabel}.title`)"
            :text="$label(`${onboardingLabel}.content`)"
          >
            <AudioPreview
              v-if="audioBookPreview && !audioBookDownloadable"
              :mp3="audioBookPreview"
              :author="book.author"
              :book-title="book.title"
              :cover="book.cover"
            />
          </OnboardingPopover>

          <div v-if="!ebookDownloadable && ebookPreview">
            <OnboardingPopover
              :is-visible="showEbookPreviewOnboarding"
              position="topleft"
              mobile-position="top"
              :title="$label(`${onboardingLabel}.title`)"
              :text="$label(`${onboardingLabel}.content`)"
            >
              <EbookPreview
                :book="book"
                :file-url="ebookPreview"
                :highlighted="showEbookPreviewOnboarding"
              />
            </OnboardingPopover>
          </div>
        </div>

        <BookLoginButtons :book="book" wrapper-class="mt-24" />
        <div v-if="audioBookDownloadable" class="mb-8">
          <p class="fw-semibold mb-16">
            {{ $label('books.audioPlayer.title') }}
          </p>
          <OnboardingPopover
            :is-visible="showAudioPlayerOnboarding"
            position="top"
            :title="$label(`${onboardingLabel}.title`)"
            :text="$label(`${onboardingLabel}.content`)"
            :extra-items="[
              {
                text: $label(`${onboardingLabel}.infoText`)
              }
            ]"
          >
            <AudioPlayerMini :book="book" />
          </OnboardingPopover>
        </div>
      </template>
    </template>
  </BookBasicInfo>
</template>

<script>
import EbookPreview from '~/components/ebook/EbookPreview'
import AudioPreview from '~/components/audio/AudioPreview'
import BookLoginButtons from '~/components/monthlyBooks/BookLoginButtons'
import BookFlipPageIcon from '~/assets/icons/book-flip-page.svg'
import DownloadModal from '~/components/monthlyBooks/DownloadModal'
import AudioPlayerMini from '~/components/audio/AudioPlayerMini'
import BookBasicInfo from '~/components/books/BookBasicInfo'
import AudioPlayerMiniSkeleton from '~/components/audio/AudioPlayerMiniSkeleton'
import OnboardingPopover from '~/components/onboarding/OnboardingPopover'
import { ONBOARDING_VARIANTS } from '~/utils/constants'

export default {
  components: {
    BookBasicInfo,
    EbookPreview,
    AudioPreview,
    BookLoginButtons,
    BookFlipPageIcon,
    DownloadModal,
    AudioPlayerMini,
    AudioPlayerMiniSkeleton,
    OnboardingPopover
  },
  props: {
    book: {
      type: Object,
      required: true
    },
    breadcrumb: {
      type: Object,
      required: true
    },
    readBookClicked: {
      type: Function,
      required: true
    },
    isAuthenticated: {
      type: Boolean,
      default: false
    },
    audioBookDownloadable: {
      type: Boolean,
      required: true
    },
    ebookDownloadable: {
      type: Boolean,
      required: true
    },
    isLoadingDetails: {
      type: Boolean,
      default: false
    },
    onboardingVariant: {
      type: String,
      required: true
    },
    onboardingStep: {
      type: Number,
      required: true
    },
    onboardingLabel: {
      type: String,
      required: true
    }
  },
  computed: {
    ebookPreview() {
      return this.book.download.ebook && this.book.download.ebook.previewFileUrl
    },
    audioBookPreview() {
      return (
        this.book.download.audioBook &&
        this.book.download.audioBook.previewFileUrl
      )
    },
    showBookPreviewSection() {
      return (
        (this.audioBookPreview || this.ebookPreview) &&
        !(this.ebookDownloadable || this.audioBookDownloadable)
      )
    },
    premiumOnabordingVariant() {
      return this.onboardingVariant === ONBOARDING_VARIANTS.premium
    },
    basicOnboardingVariant() {
      return this.onboardingVariant === ONBOARDING_VARIANTS.basic
    },
    chosenBookOnboardingVariant() {
      return this.onboardingVariant === ONBOARDING_VARIANTS.basicChosenBook
    },
    showAudioPlayerOnboarding() {
      return (
        (this.premiumOnabordingVariant && this.onboardingStep === 5) ||
        (this.chosenBookOnboardingVariant && this.onboardingStep === 3)
      )
    },
    showReadBookOnboarding() {
      return (
        (this.premiumOnabordingVariant && this.onboardingStep === 6) ||
        (this.chosenBookOnboardingVariant && this.onboardingStep === 4)
      )
    },
    showAudioPreviewOnboarding() {
      return this.basicOnboardingVariant && this.onboardingStep === 2
    },
    showEbookPreviewOnboarding() {
      return this.basicOnboardingVariant && this.onboardingStep === 3
    }
  }
}
</script>

<style lang="scss" scoped>
.open-book-icon {
  margin-right: 8px;
}

.read-book-btn {
  color: $black;
}

.bc-popover-visible {
  .audio-player,
  .btn,
  .read-book-btn {
    z-index: $popover-highlighted-el-zindex;
    position: relative;
    max-width: 100%;
    overflow: hidden;
  }

  .btn,
  .read-book-btn {
    color: $white;

    svg {
      stroke: $white;
    }
  }
}

@include media-breakpoint-only(md) {
  :deep(.onboarding-premium-5),
  :deep(.onboarding-basicChosenBook-3) {
    .bc-popover.top {
      right: calc(488px - 100vw / 2);
    }
  }
}

@include media-breakpoint-down(sm) {
  :deep(.onboarding-step-4) {
    .bc-popover.top {
      left: auto;
    }
  }
  :deep(.onboarding-basic-3) {
    .bc-popover.top {
      right: 90%;
    }
  }
}
</style>
